/**
 * Pane/User-Pane Participation Detection.
 *
 * The term "sampling" here refers to "deterministic representative selection".
 * It selects one representative mark per pane and one per user-pane to determine
 * which panes and user-panes are participating in the transition. This is used for
 * pane/non-mark animation context, no-op culling, and filtering exit marks.
 */
import { DataStorePaneState } from './DataStorePaneState';
import {
  DataStoreValue,
  getMarkIndicesForPane,
  getPaneIndicesForMarkIdsInPane,
  getUserPaneColumnIndicesForMarkIdsInPane,
  getUserPaneRowIndicesForMarkIdsInPane,
} from './DataStorePaneStateUtility';

export type PaneAnimationSamples = {
  movingPaneSampleMarkIds: number[];
  enteringPaneSampleMarkIds: number[];
  movingUserPaneSampleMarkIds: number[];
  enteringUserPaneSampleMarkIds: number[];
  exitMarkIds: number[];
};

/**
 * Returns a list of mark IDs containing exactly one representative mark
 * for each unique pane, excluding any panes represented in `marksWhosePanesToExclude`.
 */
export function sampleOnePerPaneExcludingPanesOf(
  markIds: number[],
  state: DataStorePaneState,
  paneId: string,
  marksWhosePanesToExclude: number[]
): number[] {
  if (markIds.length === 0) {
    return [];
  }
  const excludedPaneIndices = new Set(
    getPaneIndicesForMarkIdsInPane(
      paneId,
      marksWhosePanesToExclude,
      state.buildPaneIdMarkIdToPaneIndexMap.bind(state) as (paneId: string, outMap: Map<string, number>) => void
    ).filter((index: number) => index >= 0)
  );
  const paneIndices = getPaneIndicesForMarkIdsInPane(
    paneId,
    markIds,
    state.buildPaneIdMarkIdToPaneIndexMap.bind(state) as (paneId: string, outMap: Map<string, number>) => void
  );
  const firstMarkIdByPaneIndex = new Map<number, number>();
  for (let i = 0; i < markIds.length; i++) {
    const paneIndex = paneIndices[i];
    const markId = markIds[i];
    if (paneIndex == null || paneIndex < 0 || excludedPaneIndices.has(paneIndex) || firstMarkIdByPaneIndex.has(paneIndex)) {
      continue;
    }
    firstMarkIdByPaneIndex.set(paneIndex, markId);
  }
  return Array.from(firstMarkIdByPaneIndex.entries())
    .sort((left, right) => left[0] - right[0])
    .map(([, markId]) => markId);
}

/**
 * Returns a list of mark IDs containing exactly one representative mark
 * for each unique user-pane (col/row), excluding any user-panes represented in `marksWhoseUserPanesToExclude`.
 */
export function sampleOnePerUserPaneExcludingUserPanesOf(
  markIds: number[],
  state: DataStorePaneState,
  paneId: string,
  marksWhoseUserPanesToExclude: number[]
): number[] {
  if (markIds.length === 0) {
    return [];
  }
  const getMarkIndices = (id: string, ids: number[]): number[] =>
    getMarkIndicesForPane(state.getValue.bind(state) as (path: string) => DataStoreValue, id, ids);
  const excludedRowIndices = getUserPaneRowIndicesForMarkIdsInPane(
    state.getValue.bind(state) as (path: string) => DataStoreValue,
    paneId,
    marksWhoseUserPanesToExclude,
    getMarkIndices
  );
  const excludedColumnIndices = getUserPaneColumnIndicesForMarkIdsInPane(
    state.getValue.bind(state) as (path: string) => DataStoreValue,
    paneId,
    marksWhoseUserPanesToExclude,
    getMarkIndices
  );
  const excludedPairs = new Set<string>();
  for (let i = 0; i < Math.min(excludedRowIndices.length, excludedColumnIndices.length); i++) {
    const row = excludedRowIndices[i];
    const col = excludedColumnIndices[i];
    if (row == null || col == null || row < 0 || col < 0) {
      continue;
    }
    excludedPairs.add(`${col}:${row}`);
  }
  const rowIndices = getUserPaneRowIndicesForMarkIdsInPane(
    state.getValue.bind(state) as (path: string) => DataStoreValue,
    paneId,
    markIds,
    getMarkIndices
  );
  const columnIndices = getUserPaneColumnIndicesForMarkIdsInPane(
    state.getValue.bind(state) as (path: string) => DataStoreValue,
    paneId,
    markIds,
    getMarkIndices
  );
  const seenPairs = new Set<string>();
  const sampled: Array<{ col: number; row: number; markId: number }> = [];
  for (let i = 0; i < markIds.length; i++) {
    const row = rowIndices[i];
    const col = columnIndices[i];
    const markId = markIds[i];
    if (row == null || col == null || row < 0 || col < 0) {
      continue;
    }
    const key = `${col}:${row}`;
    if (excludedPairs.has(key) || seenPairs.has(key)) {
      continue;
    }
    seenPairs.add(key);
    sampled.push({ col, row, markId });
  }
  sampled.sort((left, right) => (left.col !== right.col ? left.col - right.col : left.row - right.row));
  return sampled.map((entry) => entry.markId);
}

function getUserPanePairSet(state: DataStorePaneState, paneId: string, markIds: number[]): Set<string> {
  const getMarkIndices = (id: string, ids: number[]): number[] =>
    getMarkIndicesForPane(state.getValue.bind(state) as (path: string) => DataStoreValue, id, ids);
  const rowIndices = getUserPaneRowIndicesForMarkIdsInPane(
    state.getValue.bind(state) as (path: string) => DataStoreValue,
    paneId,
    markIds,
    getMarkIndices
  );
  const columnIndices = getUserPaneColumnIndicesForMarkIdsInPane(
    state.getValue.bind(state) as (path: string) => DataStoreValue,
    paneId,
    markIds,
    getMarkIndices
  );
  const result = new Set<string>();
  for (let i = 0; i < Math.min(rowIndices.length, columnIndices.length); i++) {
    const row = rowIndices[i];
    const col = columnIndices[i];
    if (row == null || col == null || row < 0 || col < 0) {
      continue;
    }
    result.add(`${col}:${row}`);
  }
  return result;
}

function computeMarksInMovingOrEnteringPanesOfOutState(
  paneId: string,
  startState: DataStorePaneState,
  endState: DataStorePaneState,
  allMarkIds: number[],
  movingPaneSampleMarkIds: number[],
  enteringPaneSampleMarkIds: number[]
): number[] {
  let movingOrEnteringPaneIndices = new Set(
    getPaneIndicesForMarkIdsInPane(
      paneId,
      movingPaneSampleMarkIds,
      startState.buildPaneIdMarkIdToPaneIndexMap.bind(startState) as (paneId: string, outMap: Map<string, number>) => void
    ).filter((index: number) => index >= 0)
  );
  if (movingOrEnteringPaneIndices.size === 0) {
    movingOrEnteringPaneIndices = new Set(
      getPaneIndicesForMarkIdsInPane(
        paneId,
        enteringPaneSampleMarkIds,
        endState.buildPaneIdMarkIdToPaneIndexMap.bind(endState) as (paneId: string, outMap: Map<string, number>) => void
      ).filter((index: number) => index >= 0)
    );
  }
  if (movingOrEnteringPaneIndices.size === 0) {
    return allMarkIds;
  }
  const allMarkPaneIndices = getPaneIndicesForMarkIdsInPane(
    paneId,
    allMarkIds,
    startState.buildPaneIdMarkIdToPaneIndexMap.bind(startState) as (paneId: string, outMap: Map<string, number>) => void
  );
  return allMarkIds.filter((_, index) => movingOrEnteringPaneIndices.has(allMarkPaneIndices[index]));
}

function computeMarksInMovingOrEnteringUserPanesOfOutState(
  paneId: string,
  startState: DataStorePaneState,
  endState: DataStorePaneState,
  allMarkIds: number[],
  movingUserPaneSampleMarkIds: number[],
  enteringUserPaneSampleMarkIds: number[]
): number[] {
  let movingOrEnteringUserPanePairs = getUserPanePairSet(startState, paneId, movingUserPaneSampleMarkIds);
  if (movingOrEnteringUserPanePairs.size === 0) {
    movingOrEnteringUserPanePairs = getUserPanePairSet(endState, paneId, enteringUserPaneSampleMarkIds);
  }
  if (movingOrEnteringUserPanePairs.size === 0) {
    return allMarkIds;
  }
  const getMarkIndicesStart = (id: string, ids: number[]): number[] =>
    getMarkIndicesForPane(startState.getValue.bind(startState) as (path: string) => DataStoreValue, id, ids);
  const rowIndices = getUserPaneRowIndicesForMarkIdsInPane(
    startState.getValue.bind(startState) as (path: string) => DataStoreValue,
    paneId,
    allMarkIds,
    getMarkIndicesStart
  );
  const columnIndices = getUserPaneColumnIndicesForMarkIdsInPane(
    startState.getValue.bind(startState) as (path: string) => DataStoreValue,
    paneId,
    allMarkIds,
    getMarkIndicesStart
  );
  const result: number[] = [];
  for (let i = 0; i < allMarkIds.length; i++) {
    const row = rowIndices[i];
    const col = columnIndices[i];
    if (row == null || col == null || row < 0 || col < 0) {
      continue;
    }
    if (movingOrEnteringUserPanePairs.has(`${col}:${row}`)) {
      result.push(allMarkIds[i]);
    }
  }
  return result;
}

function computeMarksInMovingOrEnteringPanesAndUserPanesOfOutState(
  paneId: string,
  startState: DataStorePaneState,
  endState: DataStorePaneState,
  allMarkIds: number[],
  movingPaneSampleMarkIds: number[],
  enteringPaneSampleMarkIds: number[],
  movingUserPaneSampleMarkIds: number[],
  enteringUserPaneSampleMarkIds: number[]
): number[] {
  const marksInPanes = computeMarksInMovingOrEnteringPanesOfOutState(
    paneId,
    startState,
    endState,
    allMarkIds,
    movingPaneSampleMarkIds,
    enteringPaneSampleMarkIds
  );
  const marksInUserPanes = new Set(
    computeMarksInMovingOrEnteringUserPanesOfOutState(
      paneId,
      startState,
      endState,
      allMarkIds,
      movingUserPaneSampleMarkIds,
      enteringUserPaneSampleMarkIds
    )
  );
  return marksInPanes.filter((markId) => marksInUserPanes.has(markId));
}

/**
 * Computes representative marks for moving/entering panes and user-panes,
 * and filters the `allExitMarkIds` down to only those that belong to the
 * participating panes and user-panes. This output determines the final
 * scope of pane-level animations and culling.
 */
export function computePaneAnimationSamples(
  paneId: string,
  startState: DataStorePaneState | null,
  endState: DataStorePaneState,
  moveMarkIds: number[],
  enterMarkIds: number[],
  allExitMarkIds: number[]
): PaneAnimationSamples {
  if (startState === null) {
    return {
      movingPaneSampleMarkIds: [],
      enteringPaneSampleMarkIds: sampleOnePerPaneExcludingPanesOf(enterMarkIds, endState, paneId, []),
      movingUserPaneSampleMarkIds: [],
      enteringUserPaneSampleMarkIds: sampleOnePerUserPaneExcludingUserPanesOf(enterMarkIds, endState, paneId, []),
      exitMarkIds: [],
    };
  }

  const movingPaneSampleMarkIds = sampleOnePerPaneExcludingPanesOf(moveMarkIds, startState, paneId, []);
  const movingUserPaneSampleMarkIds = sampleOnePerUserPaneExcludingUserPanesOf(moveMarkIds, startState, paneId, []);
  const enteringPaneSampleMarkIds = sampleOnePerPaneExcludingPanesOf(enterMarkIds, endState, paneId, movingPaneSampleMarkIds);
  const enteringUserPaneSampleMarkIds = sampleOnePerUserPaneExcludingUserPanesOf(
    enterMarkIds,
    endState,
    paneId,
    movingUserPaneSampleMarkIds
  );

  return {
    movingPaneSampleMarkIds,
    enteringPaneSampleMarkIds,
    movingUserPaneSampleMarkIds,
    enteringUserPaneSampleMarkIds,
    exitMarkIds: computeMarksInMovingOrEnteringPanesAndUserPanesOfOutState(
      paneId,
      startState,
      endState,
      allExitMarkIds,
      movingPaneSampleMarkIds,
      enteringPaneSampleMarkIds,
      movingUserPaneSampleMarkIds,
      enteringUserPaneSampleMarkIds
    ),
  };
}
